"use client";

import { useCallback, useMemo } from "react";
import {
  SourceCitationBuilder,
  type SourceField,
  type SourceValues,
} from "@/components/tool-kit/source-citation-builder";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  HEARING_FIELDS as F,
  formatHearing,
  type HearingLabels,
  hearingIsReady,
} from "@/lib/tool-kit/legal-sources/congressional-hearing-citation-generator";

/** English strings. A translated content file overrides these through `ui`. */
export const CONGRESSIONAL_HEARING_CITATION_GENERATOR_STRINGS = {
  formHeading: "Hearing details",
  resultsHeading: "Your citations",
  emptyPrompt:
    "Enter the hearing title, the Congress and the hearing date to see your citations.",
  copy: "Copy",
  copied: "Copied!",
  tryExample: "Try an example",
  clear: "Clear",
  titleLabel: "Hearing title",
  titlePlaceholder:
    "Strengthening the federal student loan program for borrowers",
  titleHint:
    "Type it in sentence case, as APA wants — capitalize only the first word and proper nouns. MLA and Chicago get title case automatically.",
  shortTitleLabel: "Short title for in-text citations (optional)",
  shortTitlePlaceholder: "Strengthening the federal student loan program",
  chamberLabel: "Chamber",
  chamberSenate: "Senate",
  chamberHouse: "House of Representatives",
  chamberJoint: "Joint committee",
  committeeLabel: "Committee",
  committeePlaceholder: "Committee on Health, Education, Labor, and Pensions",
  committeeHint:
    "The full name without the chamber, e.g. Committee on the Judiciary.",
  subcommitteeLabel: "Subcommittee (optional)",
  subcommitteePlaceholder: "Subcommittee on Children and Families",
  congressLabel: "Congress",
  congressPlaceholder: "113",
  dateLabel: "Date of the hearing",
  datePlaceholder: "YYYY-MM-DD",
  printedYearLabel: "Year the hearing record was printed (Chicago)",
  printedYearPlaceholder: "2016",
  printedYearHint:
    "On the title page of the printed record. The Bluebook form uses it when it differs from the hearing year.",
  witnessLabel: "Witness (if you cite one person's testimony)",
  witnessPlaceholder: "Deanne Loonin",
  witnessRoleLabel: "Witness's title or organization (Chicago)",
  witnessRolePlaceholder: "National Consumer Law Center",
  pageLabel: "Page cited (Chicago)",
  pagePlaceholder: "40",
  publisherLabel: "Publisher (for MLA)",
  publisherPlaceholder: "U.S. Government Publishing Office",
  urlLabel: "URL",
  urlPlaceholder: "https://www.govinfo.gov/app/details/CHRG-113shrg22607",
  apaLabel: "APA 7th edition",
  mlaLabel: "MLA 9th edition",
  chicagoLabel: "Chicago 18th edition (notes)",
  referenceList: "Reference list",
  parenthetical: "In-text citation (parenthetical)",
  narrative: "In-text citation (narrative)",
  worksCited: "Works Cited",
  inText: "In-text citation",
  footnote: "Footnote",
  apaNote:
    "APA 7 (§11.6) italicizes the hearing title in sentence case and adds (testimony of …) when you cite one witness. Add a page (p. 40) to the in-text citation when you quote.",
  mlaNote:
    "MLA treats the hearing as a government document: the committee is the author and the hearing title is italic.",
  chicagoNote:
    "Chicago follows the Bluebook (CMOS 18 §14.185): the page you cite follows the Congress, the year is the year the record was printed, and the note goes in notes only.",
  example: {
    title: "Strengthening the federal student loan program for borrowers",
    shortTitle: "Strengthening the federal student loan program",
    chamber: "senate",
    committee: "Committee on Health, Education, Labor, and Pensions",
    congress: "113",
    date: "2014-03-27",
    printedYear: "2016",
    witness: "Deanne Loonin",
    witnessRole: "National Consumer Law Center",
    page: "40",
    publisher: "U.S. Government Publishing Office",
    url: "https://www.govinfo.gov/app/details/CHRG-113shrg22607",
  },
} as const;

type Strings = typeof CONGRESSIONAL_HEARING_CITATION_GENERATOR_STRINGS;

function buildFields(strings: Strings): SourceField[] {
  return [
    {
      id: F.title,
      kind: "text",
      label: strings.titleLabel,
      placeholder: strings.titlePlaceholder,
      hint: strings.titleHint,
      wide: true,
    },
    {
      id: F.chamber,
      kind: "select",
      label: strings.chamberLabel,
      options: [
        { value: "senate", label: strings.chamberSenate },
        { value: "house", label: strings.chamberHouse },
        { value: "joint", label: strings.chamberJoint },
      ],
    },
    {
      id: F.congress,
      kind: "text",
      label: strings.congressLabel,
      placeholder: strings.congressPlaceholder,
    },
    {
      id: F.committee,
      kind: "text",
      label: strings.committeeLabel,
      placeholder: strings.committeePlaceholder,
      hint: strings.committeeHint,
      wide: true,
    },
    {
      id: F.subcommittee,
      kind: "text",
      label: strings.subcommitteeLabel,
      placeholder: strings.subcommitteePlaceholder,
      wide: true,
    },
    {
      id: F.date,
      kind: "date",
      label: strings.dateLabel,
      placeholder: strings.datePlaceholder,
    },
    {
      id: F.printedYear,
      kind: "text",
      label: strings.printedYearLabel,
      placeholder: strings.printedYearPlaceholder,
      hint: strings.printedYearHint,
    },
    {
      id: F.witness,
      kind: "text",
      label: strings.witnessLabel,
      placeholder: strings.witnessPlaceholder,
    },
    {
      id: F.witnessRole,
      kind: "text",
      label: strings.witnessRoleLabel,
      placeholder: strings.witnessRolePlaceholder,
    },
    {
      id: F.page,
      kind: "text",
      label: strings.pageLabel,
      placeholder: strings.pagePlaceholder,
    },
    {
      id: F.shortTitle,
      kind: "text",
      label: strings.shortTitleLabel,
      placeholder: strings.shortTitlePlaceholder,
    },
    {
      id: F.publisher,
      kind: "text",
      label: strings.publisherLabel,
      placeholder: strings.publisherPlaceholder,
    },
    {
      id: F.url,
      kind: "url",
      label: strings.urlLabel,
      placeholder: strings.urlPlaceholder,
    },
  ];
}

/** The widget's strings, as the formatter's labels. */
export function hearingLabels(strings: Strings): HearingLabels {
  return {
    apa: strings.apaLabel,
    mla: strings.mlaLabel,
    chicago: strings.chicagoLabel,
    referenceList: strings.referenceList,
    parenthetical: strings.parenthetical,
    narrative: strings.narrative,
    worksCited: strings.worksCited,
    inText: strings.inText,
    footnote: strings.footnote,
    apaNote: strings.apaNote,
    mlaNote: strings.mlaNote,
    chicagoNote: strings.chicagoNote,
  };
}

export default function CongressionalHearingCitationGeneratorWidget() {
  const strings = useToolUi(CONGRESSIONAL_HEARING_CITATION_GENERATOR_STRINGS);
  const fields = useMemo(() => buildFields(strings), [strings]);
  const labels = useMemo(() => hearingLabels(strings), [strings]);
  const format = useCallback(
    (values: SourceValues) => formatHearing(values, labels),
    [labels]
  );

  return (
    <SourceCitationBuilder
      example={strings.example}
      fields={fields}
      format={format}
      isReady={hearingIsReady}
      strings={strings}
    />
  );
}
